import React, { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"

import HealmeAiMark from "../components/icons/HealmeAiMark"
import {
  PORTAL_AI_ASSISTANT_OPEN_REQUEST,
  type PortalAiAssistantOpenRequestDetail
} from "../constants/portalAiAssistantEvents"
import PortalAiAssistantChatPanel from "./PortalAiAssistantChatPanel"

type Props = {
  chatUrl: string
  chatStatusUrl: string
  conversationUrl: string
  conversationsUrl: string
  userId?: number
  practiceId?: number
  practiceName?: string
  practiceData?: Record<string, unknown>
}

/** This app's tailwind.config uses max-width breakpoints: `md:` = ≤767px (mobile), `min_md:` = ≥768px (desktop). */

/** Renders the assistant panel on document.body so position:fixed uses the viewport (avoids transformed ancestors). */
function AssistantPanelPortal({ children }: { children: React.ReactNode }) {
  if (typeof document === "undefined") return null
  return createPortal(children, document.body)
}

export default function PortalAiAssistantFab({
  chatUrl,
  chatStatusUrl,
  conversationUrl,
  conversationsUrl,
  userId,
  practiceId,
  practiceName,
  practiceData
}: Props) {
  const [open, setOpen] = useState(false)
  const [openRequestPrompt, setOpenRequestPrompt] = useState<string | null>(null)
  const [openRequestIntakeFormId, setOpenRequestIntakeFormId] = useState<number | null>(null)

  useEffect(() => {
    const onOpenRequest = (event: Event) => {
      const detail = (event as CustomEvent<PortalAiAssistantOpenRequestDetail>).detail
      setOpenRequestPrompt(typeof detail?.prompt === "string" ? detail.prompt : "")
      const rawId = detail?.intakeFormId
      const parsedId = rawId != null && rawId !== "" ? Number(rawId) : NaN
      setOpenRequestIntakeFormId(Number.isFinite(parsedId) ? parsedId : null)
      setOpen(true)
    }
    window.addEventListener(PORTAL_AI_ASSISTANT_OPEN_REQUEST, onOpenRequest)
    return () => window.removeEventListener(PORTAL_AI_ASSISTANT_OPEN_REQUEST, onOpenRequest)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])

  const close = useCallback(() => setOpen(false), [])
  const openChat = useCallback(() => setOpen(true), [])

  // Keep the chat panel mounted while closed so in-flight POST /assistant/chat and follow-up
  // loadConversation() still update React state; otherwise closing the FAB unmounts the tree
  // and the assistant reply never appears when the user reopens.
  const assistantPanel = (
    <AssistantPanelPortal>
      <div
        id="portal-ai-assistant-dialog"
        className={
          open
            ? "fixed bottom-20 z-[10050] flex w-full max-w-full flex-col max-h-[min(calc(100vh-7rem),640px)] md:inset-x-0 min_md:bottom-6 min_md:left-[calc(100vw-33.333333vw-1.5rem)] min_md:right-auto min_md:!h-[min(calc(100vh-5rem),620px)] min_md:!max-h-[min(calc(100vh-5rem),640px)] min_md:!w-[33.333333vw] min_md:min-w-0 min_md:max-w-[33.333333vw]"
            : "hidden"
        }
        role="dialog"
        aria-modal={false}
        aria-label="Heal.me AI Assistant"
        aria-hidden={!open}>
        <PortalAiAssistantChatPanel
          chatUrl={chatUrl}
          chatStatusUrl={chatStatusUrl}
          conversationUrl={conversationUrl}
          conversationsUrl={conversationsUrl}
          userId={userId}
          practiceId={practiceId}
          practiceName={practiceName}
          practiceData={practiceData}
          active={open}
          initialPrompt={openRequestPrompt}
          onInitialPromptApplied={() => setOpenRequestPrompt(null)}
          initialIntakeFormId={openRequestIntakeFormId}
          onInitialIntakeFormContextApplied={() => setOpenRequestIntakeFormId(null)}
          onClose={close}
        />
      </div>
    </AssistantPanelPortal>
  )

  return (
    <>
      {assistantPanel}

      {!open ? (
        <button
          type="button"
          className="fixed bottom-20 right-4 z-[10060] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-lg ring-2 ring-white/30 transition hover:scale-105 hover:from-teal-600 hover:to-teal-700 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-white motion-reduce:transition-none motion-reduce:hover:scale-100 sm:bottom-6 sm:right-6"
          aria-label="Open Heal.me AI Assistant"
          aria-expanded={false}
          aria-haspopup="dialog"
          onClick={openChat}>
          <HealmeAiMark variant="onTeal" />
        </button>
      ) : null}
    </>
  )
}
